import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription, combineLatest} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {LayoutConfigService, SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {LayoutUtilsService} from '../../../../core/_base/crud';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {BaseOfValueUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';

import {
    BaseOfValue,
    BaseOfValueOnServerCreated,
    selectBaseOfValuesActionLoading,
    selectBaseOfValueById,
    selectLastCreatedBaseOfValueId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-base-of-value-edit',
    templateUrl: './base-of-value-edit.component.html',
    styleUrls: ['./base-of-value-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})


export class BaseOfValueEditComponent implements OnInit, OnDestroy {

    model: BaseOfValue;
    oldModel: BaseOfValue;

    baseOfValueForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];
    readonly = false

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location
     * @param layoutUtilsService: LayoutUtilsService
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     * @param layoutConfigService: LayoutConfigService
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private layoutUtilsService: LayoutUtilsService,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private layoutConfigService: LayoutConfigService,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectBaseOfValuesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly ? true : false
            if (id && id > 0) {
                this.store.pipe(select(selectBaseOfValueById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initBaseOfValue();
                    }
                });
            } else {
                this.model = new BaseOfValue();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initBaseOfValue();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Bases of Values', page: `../default/admin-management/linked-tables/base-of-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/base-of-values/edit/${id}`
                    : `../default/admin-management/linked-tables/base-of-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initBaseOfValue() {
        this.createForm();
        // set subheaderService
    }


    createForm() {
        this.baseOfValueForm = this.fb.group({
            name: [this.model.name, Validators.required],
            prefix: [this.model.prefix, Validators.required],
            source: [this.model.source, Validators.required],
            description: [this.model.description, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('BASE_OF_VALUE.FORM.TITLE.EDIT', {name: this.model.name});
        }

        return this.translate.instant('BASE_OF_VALUE.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.baseOfValueForm.controls[controlName];
        const result = control.invalid && control.touched;
        return result;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.baseOfValueForm.markAsPristine();
        this.baseOfValueForm.markAsUntouched();
        this.baseOfValueForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.baseOfValueForm.controls;
        /** check form */
        if (this.baseOfValueForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateBaseOfValue(this.model);
        } else {
            this.createBaseOfValue(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: BaseOfValueModel
     */
    updateBaseOfValue(_model: BaseOfValue) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateBaseOfValue: Update<BaseOfValue> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new BaseOfValueUpdated({
            partialItem: updateBaseOfValue,
            item: _model
        }));
        this.oldModel = _model;

        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: BaseOfValueModel
     */
    createBaseOfValue(_model: BaseOfValue) {
        this.store.dispatch(new BaseOfValueOnServerCreated({item: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedBaseOfValueId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0
            && this.model.prefix && this.model.prefix.length > 0
            && this.model.description && this.model.description.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }


    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name
                || this.model.prefix != this.oldModel.prefix
                || this.model.description != this.oldModel.description
                || this.model.source != this.oldModel.source;
        }
        return false;
    }
}
